import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { RegistryOfficeItem } from 'src/app/api/models';
import { AssetManagementService, WarehouseService } from 'src/app/api/services';
import { parseErrorBody } from 'src/app/utils/utils';
import { WarehouseModalComponent } from '../warehouse-modal/warehouse-modal.component';

export interface TransferSparePartInput {
  product_id: number;
  name: string;
  brand?: string;
  serialized: boolean;
  stock_type: number;
  agreement_product_id: number | null;
  related_agreement_id: number;
  agreement_label?: string;
  stock_qty: number;
  qty: number;
  // seriali già determinati dal chiamante (es. asset di sostituzione scelto): non modificabili
  fixed_serials?: TransferSerial[];
}

export interface TransferSerial {
  code: string;
  in_stock: boolean;
}

interface TransferRow extends TransferSparePartInput {
  qtyControl: FormControl<number | null>;
  tmpSerial: FormControl<any>;
  serials: TransferSerial[];
  suggestedSerials: any[];
}

// Prodotto con più righe sulla commessa di destinazione: l'utente sceglie quale usare
interface AmbiguousAgreementProductItem {
  product_id: number;
  product_name?: string;
  candidates: {
    id: number;
    name: string | null;
    agreement_code: string;
    code_internal: string | null;
    part_number: string | null;
  }[];
  selected?: number;
}

interface TransferGroup {
  key: string;
  stock_type: number;
  related_agreement_id: number;
  label: string;
  rows: TransferRow[];
}

@Component({
  selector: 'app-modal-transfer-spare-parts',
  templateUrl: './modal-transfer-spare-parts.component.html',
  styleUrls: ['./modal-transfer-spare-parts.component.scss'],
})
export class ModalTransferSparePartsComponent {
  sourceWarehouse: RegistryOfficeItem;
  ticketId: number;
  // trasferimento fatto come sostituzione di un asset (dettaglio ticket, tab Sostituzioni)
  replacement?: { asset_to_be_replaced_id: number; temporary: boolean };
  destination?: RegistryOfficeItem;
  notes: FormControl<string | null>;
  groups: TransferGroup[] = [];
  isSubmitting = false;
  ambiguousItems: AmbiguousAgreementProductItem[] = [];
  transfersDone = 0;
  private ddtIds: number[] = [];
  private warehouseModal?: DynamicDialogRef;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private dialogService: DialogService,
    private warehouseService: WarehouseService,
    private assetManagementService: AssetManagementService,
    private messageService: MessageService
  ) {
    this.sourceWarehouse = config.data.sourceWarehouse;
    this.ticketId = config.data.ticketId;
    this.replacement = config.data.replacement;
    this.notes = new FormControl<string | null>(config.data.notes ?? null);
    this.buildGroups(config.data.products as TransferSparePartInput[]);
  }

  // Un trasferimento per ogni combinazione di utilizzo (Vendita/Manutenzione) e commessa di
  // origine: il backend registra un solo utilizzo e una sola commessa per movimento.
  private buildGroups(products: TransferSparePartInput[]) {
    const byKey = new Map<string, TransferGroup>();
    for (const p of products) {
      const key = `${p.stock_type}_${p.related_agreement_id}`;
      let group = byKey.get(key);
      if (!group) {
        group = {
          key,
          stock_type: p.stock_type,
          related_agreement_id: p.related_agreement_id,
          label: `${p.stock_type === 1 ? 'Vendita' : 'Manutenzione'}${
            p.agreement_label ? ' - commessa ' + p.agreement_label : ''
          }`,
          rows: [],
        };
        byKey.set(key, group);
      }
      group.rows.push({
        ...p,
        qtyControl: new FormControl<number | null>(p.serialized ? 0 : p.qty),
        tmpSerial: new FormControl<any>(''),
        serials: p.fixed_serials ? [...p.fixed_serials] : [],
        suggestedSerials: [],
      });
    }
    this.groups = Array.from(byKey.values());
  }

  get allRows(): TransferRow[] {
    return this.groups.flatMap(g => g.rows);
  }

  openDestinationPicker() {
    this.warehouseModal = this.dialogService.open(WarehouseModalComponent, {
      header: 'Scegli il magazzino di destinazione',
      width: '70%',
      contentStyle: { overflow: 'auto' },
      baseZIndex: 11000,
      dismissableMask: true,
      modal: true,
      data: { selectedWarehouse: this.destination, excludeWarehouseId: this.sourceWarehouse.id },
    });
    this.warehouseModal.onClose.subscribe(res => {
      if (res?.value) {
        this.destination = res.value;
      }
    });
  }

  rowQty(row: TransferRow): number {
    return row.serialized ? row.serials.length : Number(row.qtyControl.value ?? 0);
  }

  rowError(row: TransferRow): string | null {
    const qty = this.rowQty(row);
    if (row.stock_qty <= 0) {
      return 'Giacenza non disponibile';
    }
    if (qty < 1) {
      return row.serialized ? 'Aggiungi almeno un seriale' : 'Quantità non valida';
    }
    if (qty > row.stock_qty) {
      return `Quantità superiore alla giacenza (${row.stock_qty})`;
    }
    return null;
  }

  get destinationError(): string | null {
    if (!this.destination) {
      return 'Scegli il magazzino di destinazione';
    }
    if (this.destination.id === this.sourceWarehouse.id) {
      return 'Il magazzino di destinazione deve essere diverso da quello di partenza';
    }
    return null;
  }

  get canSubmit(): boolean {
    return (
      !this.isSubmitting &&
      this.allRows.length > 0 &&
      !this.destinationError &&
      this.allRows.every(r => !this.rowError(r)) &&
      this.ambiguousItems.every(a => !!a.selected)
    );
  }

  filterSerials(event: { query: string }, row: TransferRow) {
    this.assetManagementService
      .assetManagementControllerGetAll({
        limit: 100,
        skip: 0,
        related_product_id: row.product_id,
        related_warehouse_id: this.sourceWarehouse.id,
        serial_number: event.query,
      })
      .subscribe({
        next: res => {
          const already = row.serials.map(s => s.code);
          row.suggestedSerials = res.data.list
            .filter(a => a.purpose === row.stock_type)
            .filter(a => !already.includes((a.serial_number ?? '').toUpperCase()));
        },
      });
  }

  addSerialKeyDown(event: KeyboardEvent, row: TransferRow) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addSerial(row);
    }
  }

  addSerial(row: TransferRow) {
    const value = row.tmpSerial.value;
    if (!value) return;
    const raw = typeof value === 'string' ? value : value.serial_number;
    const code = (raw ?? '').trim().toUpperCase();
    if (!code) return;

    if (row.serials.some(s => s.code === code)) {
      row.tmpSerial.setValue('');
      return;
    }
    if (row.serials.length >= row.stock_qty) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aggiunta serial number',
        detail: 'Hai raggiunto la quantità massima di serial number per questo prodotto.',
      });
      return;
    }

    // Stessa verifica del trasferimento tra magazzini: se il seriale esiste nel magazzino di
    // partenza deve essere libero (senza consegna né DDT), altrimenti viene creato a destinazione.
    this.warehouseService
      .warehouseControllerIsSerialInAssetManagement({
        product_id: row.product_id,
        office_id: this.sourceWarehouse.id,
        serialNo: code,
        check_related_ddt: true,
        check_related_delivery: true,
      })
      .subscribe({
        next: r => {
          const am = r.data;
          // il DDT di un trasferimento non blocca: l'asset è in magazzino
          if (am?.related_delivery_id || (am?.related_ddt?.id && !am.related_ddt_is_transfer)) {
            this.messageService.add({
              severity: 'error',
              summary: 'Aggiunta serial number',
              detail: r.message,
            });
            row.tmpSerial.setValue('');
            return;
          }
          row.serials.push({ code, in_stock: true });
          row.tmpSerial.setValue('');
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Aggiunta serial number',
            detail: `Il serial number ${code} non è presente nel magazzino di partenza: verrà creato nel magazzino di destinazione.`,
          });
          row.serials.push({ code, in_stock: false });
          row.tmpSerial.setValue('');
        },
      });
  }

  removeSerial(row: TransferRow, index: number) {
    row.serials.splice(index, 1);
  }

  removeRow(group: TransferGroup, row: TransferRow) {
    group.rows = group.rows.filter(r => r !== row);
    this.groups = this.groups.filter(g => g.rows.length > 0);
  }

  close() {
    this.ref.close(
      this.transfersDone > 0
        ? { done: true, transfers: this.transfersDone, ddt_ids: this.ddtIds }
        : undefined
    );
  }

  async confirm() {
    if (!this.canSubmit) return;
    this.isSubmitting = true;
    const totalTransfers = this.groups.length;

    // I gruppi già eseguiti vengono tolti dall'elenco: in caso di errore su uno successivo,
    // un nuovo tentativo non ripete i trasferimenti già registrati.
    for (const group of [...this.groups]) {
      try {
        const res: any = await firstValueFrom(
          this.warehouseService.warehouseControllerCreateTransfer({
            body: {
              from_registry_office_id: this.sourceWarehouse.id,
              to_registry_office_id: this.destination!.id,
              related_agreement_id: group.related_agreement_id,
              transfer_type: 'ALL',
              notes: this.notes.value ?? undefined,
              related_ticket_id: this.ticketId,
              ...(this.replacement && {
                replacement_for_asset_id: this.replacement.asset_to_be_replaced_id,
                temporary_replacement: this.replacement.temporary,
              }),
              items: group.rows.map(r => ({
                stock_type: r.stock_type,
                product_id: r.product_id,
                agreement_product_id: r.agreement_product_id ?? undefined,
                dest_agreement_product_id:
                  this.ambiguousItems.find(a => a.product_id === r.product_id)?.selected ?? undefined,
                qty: this.rowQty(r),
                serializable: r.serialized,
                serial_numbers: r.serialized ? r.serials : [],
              })),
            } as any,
          })
        );
        this.transfersDone++;
        if (res?.data?.ddt_id) {
          this.ddtIds.push(res.data.ddt_id);
        }
        this.groups = this.groups.filter(g => g !== group);
      } catch (err) {
        const errorBody = parseErrorBody(err);

        // Il backend non sa su quale riga prodotto della commessa di destinazione attribuire
        // uno o più prodotti: l'utente sceglie e poi si riprova (i gruppi già eseguiti non si ripetono)
        if (errorBody?.message?.code === 'AMBIGUOUS_AGREEMENT_PRODUCT') {
          this.ambiguousItems = errorBody.message.items.map((item: any) => ({
            ...item,
            product_name: this.allRows.find(r => r.product_id === item.product_id)?.name,
            selected: this.ambiguousItems.find(a => a.product_id === item.product_id)?.selected,
          }));
          this.messageService.add({
            severity: 'info',
            summary: 'Trasferimento',
            detail:
              'Scegli il prodotto della commessa di destinazione a cui attribuire il trasferimento, poi conferma di nuovo.',
            life: 8000,
          });
          this.isSubmitting = false;
          return;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Trasferimento',
          detail:
            (typeof errorBody?.message === 'string' ? errorBody.message : 'Si è verificato un errore imprevisto.') +
            (this.transfersDone > 0
              ? ` Eseguiti ${this.transfersDone} trasferimenti su ${totalTransfers}: i restanti sono ancora da fare.`
              : ''),
          life: 10000,
        });
        this.isSubmitting = false;
        return;
      }
    }

    this.isSubmitting = false;
    this.ref.close({ done: true, transfers: this.transfersDone, ddt_ids: this.ddtIds });
  }
}
